import {useCallback, useEffect, useRef, useState} from 'react'
import {useTranslation} from '@/translations/i18n.tsx'
import {ChevronDownIcon, ChevronUpIcon, Send01Icon} from '@/components/ui/icon/Icon'
import {Avatar} from '@/components/ui/avatar/Avatar'
import {Skeleton} from '@/components/ui/skeleton/Skeleton'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {MailerRecipientGroup, MailerRecipients} from '@/features/mailer/types'
import {PREVIEW_SAMPLE_SIZE} from '@/features/mailer/constants'
import {useMailerRecipientsList} from '@/features/mailer/queries/useMailerRecipientsList'
import {
    StyledCounterBand,
    StyledCounterCard,
    StyledCounterIconCircle,
    StyledCounterInfo,
    StyledCounterTexts,
    StyledKindBadge,
    StyledPreviewEmpty,
    StyledPreviewHeader,
    StyledPreviewIdentity,
    StyledPreviewList,
    StyledPreviewRow,
    StyledPreviewScrollArea,
    StyledPreviewSection,
    StyledPreviewTab,
    StyledPreviewTabCounter,
    StyledPreviewTabs,
    StyledPreviewToggle,
    StyledSkeletonRow
} from '@/features/mailer/components/mailer-recipient-counter/style'

interface MailerRecipientCounterProps {
    total: number
    patientsCount: number | null
    consultantsCount: number | null
    isLoading: boolean
    recipients: MailerRecipients | null
    includePatients: boolean
    includeConsultants: boolean
}

// Live recipient counter (design §5.8): summary band with deduped total and
// per-category split, plus an expandable browsing panel — one tab per group
// when both are active — with server-paginated infinite scroll (OPSSTD-3649).
export const MailerRecipientCounter = ({
    total,
    patientsCount,
    consultantsCount,
    isLoading,
    recipients,
    includePatients,
    includeConsultants
}: MailerRecipientCounterProps) => {
    const {t} = useTranslation()
    const [isPreviewOpen, setIsPreviewOpen] = useState(false)
    const [activeGroup, setActiveGroup] = useState<MailerRecipientGroup>(includePatients ? 'patients' : 'consultants')
    const endOfListRef = useRef<HTMLDivElement>(null)

    // Keep the active tab valid if its category gets deselected while the panel is open.
    useEffect(() => {
        if (activeGroup === 'patients' && !includePatients && includeConsultants) setActiveGroup('consultants')
        if (activeGroup === 'consultants' && !includeConsultants && includePatients) setActiveGroup('patients')
    }, [activeGroup, includePatients, includeConsultants])

    const listQuery = useMailerRecipientsList({
        recipients,
        group: activeGroup,
        enabled: isPreviewOpen && (activeGroup === 'patients' ? includePatients : includeConsultants)
    })

    const handleObserver = useCallback(
        (entries: IntersectionObserverEntry[]) => {
            const [entry] = entries
            if (entry.isIntersecting && listQuery.hasNextPage && !listQuery.isFetchingNextPage) {
                listQuery.fetchNextPage()
            }
        },
        [listQuery]
    )

    useEffect(() => {
        if (!isPreviewOpen || !listQuery.hasNextPage || !endOfListRef.current) return
        const node = endOfListRef.current
        const observer = new IntersectionObserver(handleObserver, {root: null, rootMargin: '0px', threshold: 0.5})
        observer.observe(node)
        return () => observer.unobserve(node)
    }, [handleObserver, isPreviewOpen, listQuery.hasNextPage, listQuery.recipients.length])

    const splitParts = [
        patientsCount !== null ? t('mailer:counter_split_patients', {count: patientsCount}) : null,
        consultantsCount !== null ? t('mailer:counter_split_staff', {count: consultantsCount}) : null
    ].filter(Boolean)

    // The BE dedupes emails shared across the two lists (contract §1):
    // surfacing the overlap explains why total < patients + consultants.
    const overlap =
        patientsCount !== null && consultantsCount !== null ? Math.max(0, patientsCount + consultantsCount - total) : 0

    const activeTotal = (activeGroup === 'patients' ? patientsCount : consultantsCount) ?? 0
    const listItems = listQuery.recipients
    const isListInitialLoading = listQuery.isLoading && listItems.length === 0

    return (
        <StyledCounterCard>
            <StyledCounterBand>
                <StyledCounterInfo>
                    <StyledCounterIconCircle>
                        {isLoading ? <Spinner size={16} /> : <Send01Icon />}
                    </StyledCounterIconCircle>
                    <StyledCounterTexts>
                        <p>
                            <strong>{total.toLocaleString('it-IT')}</strong> {t('mailer:counter_recipients_match')}
                        </p>
                        <small>
                            {splitParts.length > 0 ? splitParts.join(' + ') : t('mailer:counter_none_selected')}
                            {overlap > 0 && ` · ${t('mailer:counter_overlap', {count: overlap})}`}
                        </small>
                    </StyledCounterTexts>
                </StyledCounterInfo>
                <StyledPreviewToggle
                    type="button"
                    aria-expanded={isPreviewOpen}
                    aria-controls="mailer-recipients-preview"
                    onClick={() => setIsPreviewOpen(previous => !previous)}
                >
                    {isPreviewOpen ? t('mailer:counter_preview_hide') : t('mailer:counter_preview_show')}
                    {isPreviewOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
                </StyledPreviewToggle>
            </StyledCounterBand>

            {isPreviewOpen && (
                <StyledPreviewSection id="mailer-recipients-preview">
                    <StyledPreviewHeader>
                        <strong>{t('mailer:preview_header')}</strong>
                        {activeTotal > 0 && (
                            <span>
                                {t('mailer:preview_showing', {
                                    count: Math.min(listItems.length, activeTotal),
                                    total: activeTotal.toLocaleString('it-IT')
                                })}
                            </span>
                        )}
                    </StyledPreviewHeader>

                    {includePatients && includeConsultants && (
                        <StyledPreviewTabs>
                            <StyledPreviewTab
                                type="button"
                                $active={activeGroup === 'patients'}
                                onClick={() => setActiveGroup('patients')}
                            >
                                {t('mailer:recipients_patients')}
                                <StyledPreviewTabCounter $active={activeGroup === 'patients'}>
                                    {patientsCount ?? 0}
                                </StyledPreviewTabCounter>
                            </StyledPreviewTab>
                            <StyledPreviewTab
                                type="button"
                                $active={activeGroup === 'consultants'}
                                onClick={() => setActiveGroup('consultants')}
                            >
                                {t('mailer:recipients_staff')}
                                <StyledPreviewTabCounter $active={activeGroup === 'consultants'}>
                                    {consultantsCount ?? 0}
                                </StyledPreviewTabCounter>
                            </StyledPreviewTab>
                        </StyledPreviewTabs>
                    )}

                    {isListInitialLoading ? (
                        Array.from({length: PREVIEW_SAMPLE_SIZE}, (_, index) => (
                            <StyledSkeletonRow key={index}>
                                <Skeleton width={24} height={24} />
                                <Skeleton height={14} width="60%" />
                                <Skeleton height={18} width={56} />
                            </StyledSkeletonRow>
                        ))
                    ) : listItems.length === 0 ? (
                        <StyledPreviewEmpty>{t('mailer:preview_empty')}</StyledPreviewEmpty>
                    ) : (
                        <StyledPreviewScrollArea>
                            <StyledPreviewList>
                                {listItems.map(recipient => (
                                    <StyledPreviewRow key={recipient.entityId}>
                                        <Avatar size="xs" name={recipient.name || recipient.email} />
                                        <StyledPreviewIdentity>
                                            <strong className={recipient.name ? undefined : 'missing'}>
                                                {recipient.name || t('mailer:preview_name_missing')}
                                            </strong>
                                            <span>{recipient.email}</span>
                                        </StyledPreviewIdentity>
                                        <StyledKindBadge $kind={recipient.kind}>
                                            {recipient.kind === 'patient'
                                                ? t('mailer:preview_kind_patient')
                                                : t('mailer:preview_kind_consultant')}
                                        </StyledKindBadge>
                                    </StyledPreviewRow>
                                ))}
                            </StyledPreviewList>
                            {(listQuery.hasNextPage || listQuery.isFetchingNextPage) && (
                                <div ref={endOfListRef} style={{height: 20, padding: '10px 0'}}>
                                    {listQuery.isFetchingNextPage && (
                                        <StyledSkeletonRow>
                                            <Spinner size={16} />
                                        </StyledSkeletonRow>
                                    )}
                                </div>
                            )}
                        </StyledPreviewScrollArea>
                    )}
                </StyledPreviewSection>
            )}
        </StyledCounterCard>
    )
}

MailerRecipientCounter.displayName = 'MailerRecipientCounter'
